/**
 * Outil « Constructeur d'intervalles » — calculs (pur).
 *
 * Un intervalle de ℝ : deux bornes, chacune finie (ouverte ou fermée) ou
 * infinie (toujours ouverte). Notation française « [−2 ; 3[ », inégalité
 * « −2 ≤ x < 3 », appartenance, intersection, réunion (un ou deux
 * morceaux), et |x − a| ≤ r ⇔ a − r ≤ x ≤ a + r.
 */

export interface Bound {
  v: number;
  closed: boolean;
}

/** `lo` / `hi` null : −∞ / +∞ */
export interface Interval {
  lo: Bound | null;
  hi: Bound | null;
}

const num = (v: number) => {
  const s = String(Number(v.toFixed(6)))
    .replace('.', ',')
    .replace('-', '−');
  return s;
};

export function isEmpty(I: Interval): boolean {
  if (!I.lo || !I.hi) return false;
  if (I.lo.v > I.hi.v) return true;
  return I.lo.v === I.hi.v && !(I.lo.closed && I.hi.closed);
}

/** « [−2 ; 3[ », « ]−∞ ; 5] », « ℝ », « {2} », « ∅ » */
export function notation(I: Interval): string {
  if (isEmpty(I)) return '∅';
  if (!I.lo && !I.hi) return 'ℝ';
  if (I.lo && I.hi && I.lo.v === I.hi.v) return `{${num(I.lo.v)}}`;
  const left = I.lo ? `${I.lo.closed ? '[' : ']'}${num(I.lo.v)}` : ']−∞';
  const right = I.hi ? `${num(I.hi.v)}${I.hi.closed ? ']' : '['}` : '+∞[';
  // espaces insécables : « ]−∞ ; −1] » ne se coupe pas en fin de ligne
  return `${left}\u00a0;\u00a0${right}`;
}

/** « −2 ≤ x < 3 », « x ≤ 5 », « x > 1 », « x ∈ ℝ » */
export function inequality(I: Interval, x = 'x'): string {
  if (isEmpty(I)) return 'aucun réel';
  if (!I.lo && !I.hi) return `${x} ∈ ℝ`;
  if (I.lo && I.hi && I.lo.v === I.hi.v) return `${x} = ${num(I.lo.v)}`;
  const l = I.lo ? `${num(I.lo.v)} ${I.lo.closed ? '≤' : '<'} ` : '';
  const r = I.hi ? ` ${I.hi.closed ? '≤' : '<'} ${num(I.hi.v)}` : '';
  if (I.lo && !I.hi) return `${x} ${I.lo.closed ? '≥' : '>'} ${num(I.lo.v)}`;
  return `${l}${x}${r}`;
}

export function contains(I: Interval, x: number): boolean {
  if (isEmpty(I)) return false;
  const okLo = !I.lo || x > I.lo.v || (I.lo.closed && x === I.lo.v);
  const okHi = !I.hi || x < I.hi.v || (I.hi.closed && x === I.hi.v);
  return okLo && okHi;
}

/** la plus grande des bornes basses (null = −∞ est la plus petite) */
function maxLo(a: Bound | null, b: Bound | null): Bound | null {
  if (!a) return b;
  if (!b) return a;
  if (a.v !== b.v) return a.v > b.v ? a : b;
  return { v: a.v, closed: a.closed && b.closed };
}
function minHi(a: Bound | null, b: Bound | null): Bound | null {
  if (!a) return b;
  if (!b) return a;
  if (a.v !== b.v) return a.v < b.v ? a : b;
  return { v: a.v, closed: a.closed && b.closed };
}
function minLo(a: Bound | null, b: Bound | null): Bound | null {
  if (!a || !b) return null;
  if (a.v !== b.v) return a.v < b.v ? a : b;
  return { v: a.v, closed: a.closed || b.closed };
}
function maxHi(a: Bound | null, b: Bound | null): Bound | null {
  if (!a || !b) return null;
  if (a.v !== b.v) return a.v > b.v ? a : b;
  return { v: a.v, closed: a.closed || b.closed };
}

/** I ∩ J (peut être vide : on renvoie l'intervalle vide tel quel, cf. isEmpty) */
export function intersect(I: Interval, J: Interval): Interval {
  return { lo: maxLo(I.lo, J.lo), hi: minHi(I.hi, J.hi) };
}

/** I ∪ J : un intervalle si les morceaux se touchent ou se chevauchent, sinon deux (dans l'ordre). */
export function union(I: Interval, J: Interval): Interval[] {
  const parts = [I, J].filter((K) => !isEmpty(K));
  if (parts.length < 2) return parts;
  const [a, b] = [...parts].sort(
    (p, q) => (p.lo ? p.lo.v : -Infinity) - (q.lo ? q.lo.v : -Infinity),
  );
  // b commence-t-il avant la fin de a (ou pile au bout, l'un des deux comprenant le point) ?
  const touch =
    !a!.hi ||
    !b!.lo ||
    b!.lo.v < a!.hi.v ||
    (b!.lo.v === a!.hi.v && (b!.lo.closed || a!.hi.closed));
  if (!touch) return [a!, b!];
  return [{ lo: minLo(a!.lo, b!.lo), hi: maxHi(a!.hi, b!.hi) }];
}

/** |x − a| ≤ r (ou < r) : l'intervalle centré en a de rayon r */
export function absInterval(a: number, r: number, strict: boolean): Interval {
  return { lo: { v: a - r, closed: !strict }, hi: { v: a + r, closed: !strict } };
}

/** |x − a| ≥ r (ou > r) : en dehors, deux morceaux */
export function absOutside(a: number, r: number, strict: boolean): Interval[] {
  return [
    { lo: null, hi: { v: a - r, closed: !strict } },
    { lo: { v: a + r, closed: !strict }, hi: null },
  ];
}

export function unionText(parts: readonly Interval[]): string {
  if (parts.length === 0) return '∅';
  return parts.map(notation).join(' ∪ ');
}

export { num as formatBound };
